/**
 * TaskDetailDialog — Popup dialog showing full details of a single task
 *
 * Displays metadata, phase stepper, error info, token usage, result, and entity link.
 */

import React, { useState, useMemo, createElement } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Divider,
  IconButton,
  SvgIconProps,
  TextField,
  Alert,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { BaseDialog } from '@modules/ui-components/dialogs/BaseDialog/BaseDialog.view';
import { AgentApiClient } from '@modules/content-agent';
import { useTask } from '../hooks/useTask';
import { TASK_TYPE_CONFIG, STATUS_COLORS, STATUS_LABELS } from '../constants/taskTypeConfig';
import { TASKS_PANEL } from '../selectors/tasks-panel.sel';
import type { TaskPhaseDefinition } from '../types';
import {
  TaskErrorResolver,
  taskErrorResolverOffersOpenEntity,
  useTaskErrorResolverPanelLang,
} from './TaskErrorResolver';
import { AiCallDebugBlocks } from './AiCallsList';
import { StrategyHealthReportView } from './StrategyHealthReportView';
import { useTaskAiCalls, type AiCallDoc } from '../hooks/useTaskAiCalls';
import type { BackendTask } from '../types';
import {
  resolveCliStrategyHealthFindingTarget,
  type CliRepairReport,
  type CliStrategyHealthFinding,
  type CliStrategyHealthReport,
} from '@modules/shared-types';

export type TaskResultActionsRenderer = (task: BackendTask) => React.ReactNode;

interface TaskDetailDialogProps {
  taskId: string | null;
  open: boolean;
  onClose: () => void;
  onNavigate?: (frontendUrl: string) => void;
  renderResultActions?: TaskResultActionsRenderer;
}

// Phase row — mirrors pattern from TaskItem.tsx, expandable to show metadata
const PhaseRow: React.FC<{
  phaseKey: string;
  phase: TaskPhaseDefinition;
  aiCalls: AiCallDoc[];
}> = ({
  phaseKey,
  phase,
  aiCalls,
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasMetadata = Boolean(phase.metadata && Object.keys(phase.metadata).length > 0);
  const hasAiCalls = aiCalls.length > 0;
  const canExpand = hasMetadata || hasAiCalls;

  const icon =
    phase.status === 'completed' ? '✓'
    : phase.status === 'running' ? '●'
    : phase.status === 'error' ? '✗'
    : phase.status === 'skipped' ? '—'
    : '○';

  const color =
    phase.status === 'completed' ? 'success.main'
    : phase.status === 'running' ? 'primary.main'
    : phase.status === 'error' ? 'error.main'
    : 'text.disabled';

  const formatTime = (d: Date | undefined | null) => {
    if (!d || !(d instanceof Date)) return null;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.5,
          cursor: canExpand ? 'pointer' : 'default',
        }}
        onClick={() => canExpand && setExpanded(!expanded)}
        data-testid={`${TASKS_PANEL.phaseItemPrefix}${phaseKey}`}
      >
        <Typography variant="body2" sx={{ color, minWidth: 16, textAlign: 'center' }}>
          {icon}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            color: phase.status === 'running' ? 'text.primary' : 'text.secondary',
          }}
        >
          {phase.displayName ?? phase.name}
        </Typography>
        {formatTime(phase.startedAt as Date | undefined) && (
          <Typography variant="caption" color="text.disabled">
            {formatTime(phase.startedAt as Date | undefined)}
          </Typography>
        )}
        {phase.error && (
          <Typography variant="caption" color="error" sx={{ maxWidth: 200 }} noWrap>
            {phase.error}
          </Typography>
        )}
        {canExpand && (
          <Typography variant="caption" color="text.disabled">
            {expanded ? '\u25BE' : '\u25B8'}
          </Typography>
        )}
      </Box>
      {expanded && (
        <Box sx={{ ml: 4, mt: 0.5, mb: 1 }}>
          {phase.metadata?.usage && (
            <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                In: {(phase.metadata.usage as any).inputTokens?.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Out: {(phase.metadata.usage as any).outputTokens?.toLocaleString()}
              </Typography>
              {phase.metadata.model && (
                <Typography variant="caption" color="text.secondary">
                  Model: {phase.metadata.model}
                </Typography>
              )}
            </Box>
          )}
          {hasMetadata && (
            <Box
              sx={{
                p: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                maxHeight: 300,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(phase.metadata, null, 2)}
            </Box>
          )}
          {hasAiCalls && (
            <Box sx={{ mt: hasMetadata ? 1 : 0 }}>
              <AiCallDebugBlocks
                calls={aiCalls}
                title={`AI debug for this phase (${aiCalls.length})`}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

function formatFullTimestamp(date: Date | undefined | null): string {
  if (!date || !(date instanceof Date)) return '—';
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

interface TaskBillingInfo {
  currency?: string;
  customerAmountMinor?: number;
  creditDebitMinor?: number;
  quotaQuantity?: number;
  creditQuantity?: number;
  sourceHandler?: string;
  usageEventId?: string;
}

function getTaskBilling(task: BackendTask): TaskBillingInfo | undefined {
  return (task as unknown as { billing?: TaskBillingInfo }).billing;
}

function formatMoneyMinor(amountMinor: number | undefined, currency = 'usd'): string {
  if (amountMinor === undefined || !Number.isFinite(amountMinor)) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountMinor / 100);
}

type RepairGroup = {
  key: string;
  label: string;
  target: { kind: 'common' } | { kind: 'task'; taskId: string };
  issues: Array<Record<string, any>>;
};

function inferIssueTaskId(issue: Record<string, any>, report: Record<string, any>): string | null {
  if (typeof issue.taskId === 'string' && issue.taskId) return issue.taskId;
  const block = typeof issue.block === 'string' ? issue.block : '';
  const blockReport = Array.isArray(report.blocks)
    ? report.blocks.find((entry: Record<string, any>) => entry.blockId === block)
    : null;
  if (typeof blockReport?.taskId === 'string' && blockReport.taskId) return blockReport.taskId;
  if (block.includes('_step_')) return block.split('_step_')[0] || null;
  return null;
}

function taskNameMap(task: BackendTask): Map<string, string> {
  const inputTasks = ((task as any).phases?.cleaning?.metadata?.input?.sourceData?.tasks ?? []) as Array<Record<string, any>>;
  const entries: Array<[string, string]> = inputTasks.map((entry) => {
    const id = String(entry.id ?? entry.entityId ?? '').trim();
    return [id, String(entry.name ?? entry.title ?? id)] as [string, string];
  }).filter(([id]) => Boolean(id));
  return new Map(entries);
}

function buildRepairGroups(task: BackendTask): RepairGroup[] {
  const report = ((task as any).contractCompileReport ?? {}) as Record<string, any>;
  const issues = Array.isArray(report.issues)
    ? report.issues.filter((issue: Record<string, any>) => issue.severity === 'error')
    : [];
  const names = taskNameMap(task);
  const grouped = new Map<string, RepairGroup>();
  for (const issue of issues) {
    const taskId = inferIssueTaskId(issue, report);
    const key = taskId ? `task:${taskId}` : 'common';
    const existing = grouped.get(key);
    if (existing) {
      existing.issues.push(issue);
      continue;
    }
    grouped.set(key, taskId
      ? {
          key,
          label: names.get(taskId) || taskId,
          target: { kind: 'task', taskId },
          issues: [issue],
        }
      : {
          key,
          label: 'Common Welcome and Goals',
          target: { kind: 'common' },
          issues: [issue],
        });
  }
  return Array.from(grouped.values());
}

export const StrategyContractRepairPanel: React.FC<{ task: BackendTask }> = ({ task }) => {
  const report = (task as any).contractCompileReport;
  const partialBlocks = (task as any).partialResult?.onboardingStrategySequence;
  const projectId = (task as any).projectId as string | undefined;
  const strategyId = (task as any).strategyId as string | undefined;
  const canRepair = Boolean(report && report.valid === false && Array.isArray(partialBlocks) && partialBlocks.length > 0 && projectId && strategyId);
  const groups = useMemo(() => buildRepairGroups(task), [task]);
  const [instructions, setInstructions] = useState<Record<string, string>>({});
  const [runningKey, setRunningKey] = useState<string | null>(null);
  const [message, setMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

  if (!report || report.valid !== false) return null;

  const startRepair = async (group: RepairGroup) => {
    if (!projectId || !strategyId) return;
    setRunningKey(group.key);
    setMessage(null);
    try {
      const client = new AgentApiClient();
      const response = await client.repairStrategyContract({
        projectId,
        strategyId,
        sourceTaskId: task.id,
        target: group.target,
        userId: (task as any).userId,
        repairInstruction: instructions[group.key]?.trim() || undefined,
      });
      if (!response.success || !response.taskId) {
        throw new Error(response.error || 'Repair task was not started');
      }
      setMessage({ severity: 'success', text: `Repair task started: ${response.taskId}` });
    } catch (error: any) {
      setMessage({ severity: 'error', text: error?.message || 'Failed to start repair task' });
    } finally {
      setRunningKey(null);
    }
  };

  return (
    <Box data-testid="strategy-contract-repair-panel">
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Strategy Contract Repair
      </Typography>
      {!canRepair && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Repair is unavailable for this task because it lacks draft blocks or strategy metadata.
        </Alert>
      )}
      {message && (
        <Alert severity={message.severity} sx={{ mb: 1 }} data-testid="strategy-contract-repair-message">
          {message.text}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {groups.map((group) => (
          <Box
            key={group.key}
            sx={{
              p: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
            data-testid={`strategy-contract-repair-group-${group.key.replace(':', '-')}`}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                {group.label}
              </Typography>
              <Chip size="small" color="error" variant="outlined" label={`${group.issues.length} issue${group.issues.length === 1 ? '' : 's'}`} />
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2, color: 'text.secondary' }}>
              {group.issues.slice(0, 3).map((issue, index) => (
                <Typography component="li" variant="caption" key={`${group.key}-${index}`}>
                  [{issue.code || 'contract'}] {issue.message}
                </Typography>
              ))}
            </Box>
            <TextField
              value={instructions[group.key] || ''}
              onChange={(event) => setInstructions((current) => ({ ...current, [group.key]: event.target.value }))}
              placeholder="Optional repair instruction"
              size="small"
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 1 }}
              inputProps={{ 'data-testid': `strategy-contract-repair-instruction-${group.key.replace(':', '-')}` }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => startRepair(group)}
              disabled={!canRepair || runningKey !== null}
              sx={{ mt: 1, textTransform: 'none' }}
              data-testid={`strategy-contract-repair-run-${group.key.replace(':', '-')}`}
            >
              {runningKey === group.key ? 'Starting...' : 'Regenerate affected block group'}
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const StrategyHealthTaskSection: React.FC<{
  task: BackendTask;
  report: CliStrategyHealthReport;
  onNavigate?: (frontendUrl: string) => void;
}> = ({ task, report, onNavigate }) => {
  const projectId = (task as any).projectId as string | undefined;
  const strategyId = (task as any).strategyId as string | undefined;

  const handleFindingGo = (finding: CliStrategyHealthFinding) => {
    if (!onNavigate || !projectId || !strategyId) return;
    const target = resolveCliStrategyHealthFindingTarget(finding);
    const query = new URLSearchParams();
    if (target.blockKey) query.set('block', target.blockKey);
    if (target.screenId) query.set('screen', target.screenId);
    const queryString = query.toString();
    onNavigate(`/project/${encodeURIComponent(projectId)}/strategy/${encodeURIComponent(strategyId)}${queryString ? `?${queryString}` : ''}`);
  };

  return (
    <Box>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Strategy Health Report</Typography>
      <StrategyHealthReportView report={report} onFindingGo={handleFindingGo} />
    </Box>
  );
};

export const StrategyRepairReportSection: React.FC<{ report: CliRepairReport }> = ({ report }) => {
  const actions = report.executedActions ?? report.plannedActions ?? [];
  // PC80-07: a completed run is NOT semantic acceptance while findings are
  // unresolved, surviving or surfaced. `finalHealth` carries that verdict;
  // legacy reports without it keep the historic deterministic-only rendering.
  const finalHealth = report.finalHealth;
  const repairIncomplete = finalHealth ? !finalHealth.repairComplete : false;
  const openFindings = finalHealth
    ? [
      finalHealth.unresolvedFindings > 0 ? `${finalHealth.unresolvedFindings} unresolved` : null,
      finalHealth.survivedFindings > 0 ? `${finalHealth.survivedFindings} surviving` : null,
      finalHealth.surfacedActions > 0 ? `${finalHealth.surfacedActions} surfaced action${finalHealth.surfacedActions === 1 ? '' : 's'}` : null,
    ].filter(Boolean).join(' / ')
    : '';
  return (
    <Box data-testid={TASKS_PANEL.strategyRepairReportSection}>
      <Divider sx={{ my: 1.5 }} />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>Strategy Repair Report</Typography>
        <Chip size="small" variant="outlined" label={report.mode} />
        <Chip
          size="small"
          color={report.status === 'failed'
            ? 'error'
            : (report.status === 'refused' || repairIncomplete) ? 'warning' : 'success'}
          label={repairIncomplete ? `${report.status} · repair incomplete` : report.status}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Scope: {report.screenId ? `screen ${report.screenId} in block ${report.blockId}` : report.blockId ? `block ${report.blockId}` : 'whole strategy'}
        {' · '}Model: {report.provider}/{report.model}
      </Typography>

      {report.planSummary && (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          <Chip size="small" label={`${report.planSummary.findingsTotal} findings`} />
          <Chip size="small" label={`${report.planSummary.autofixes} autofixes`} />
          <Chip size="small" label={`${report.planSummary.retries} retries`} />
          <Chip size="small" label={`${report.planSummary.surfaced} surfaced`} />
        </Stack>
      )}

      {report.verdictDeltas && (
        <Alert
          severity={finalHealth
            ? (finalHealth.status === 'passed' ? 'success' : 'warning')
            : (report.verdictDeltas.healthFindingsAfter === 0 ? 'success' : 'info')}
          sx={{ mb: 1 }}
          {...(finalHealth ? { 'data-testid': TASKS_PANEL.strategyRepairReportFinalHealth } : {})}
        >
          Health findings: {report.verdictDeltas.healthFindingsBefore} → {report.verdictDeltas.healthFindingsAfter ?? 'not available'}
          {finalHealth && repairIncomplete && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              {openFindings || 'open findings'} — repair incomplete
            </Typography>
          )}
        </Alert>
      )}

      {report.planReceipt?.checksum && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">Plan checksum</Typography>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}
            data-testid={TASKS_PANEL.strategyRepairReportChecksum}
          >
            {report.planReceipt.checksum}
          </Typography>
        </Box>
      )}
      {report.planApproval && (
        <Alert severity="success" sx={{ mb: 1 }} data-testid={TASKS_PANEL.strategyRepairReportApproval}>
          Approved checksum echoed and matched: {report.planApproval.approvedPlanChecksum}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Retry budget: {report.budgetUsage.perBlockRetries} per block · {Object.keys(report.budgetUsage.dispatched).length} blocks dispatched
        {report.budgetUsage.attemptedFindings?.length ? ` · ${report.budgetUsage.attemptedFindings.length} finding attempts` : ''}
      </Typography>

      <Stack spacing={0.75}>
        {actions.map((action, index) => {
          const executed = 'outcome' in action ? action : null;
          return (
            <Box
              key={`${action.kind}-${index}`}
              sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              data-testid={TASKS_PANEL.strategyRepairReportAction(index)}
            >
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                <Chip size="small" label={action.kind} />
                {executed && <Chip size="small" variant="outlined" label={executed.outcome} />}
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {action.target.screenId ? `screen ${action.target.screenId}` : action.target.blockKey ? `block ${action.target.blockKey}` : 'strategy'}
                </Typography>
              </Stack>
              {/* Ruling GP-R28: a repair that STEPPED ASIDE says why — G3b's
                  «edited during generation — repair skipped» and «invalid block
                  (persistence preflight failed) — repair skipped» are only
                  honest when the owner can read them in the dialog. */}
              {action.reason && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Reason: {action.reason}
                </Typography>
              )}
              {executed?.taskId && <Typography variant="caption" sx={{ display: 'block' }}>Retry task: {executed.taskId}</Typography>}
              {executed?.reviewTaskId && <Typography variant="caption" sx={{ display: 'block' }}>Post-retry review: {executed.reviewTaskId}</Typography>}
              {executed?.before && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Scoped health: {executed.before.healthFindings} → {executed.after?.healthFindings ?? 'not available'}
                </Typography>
              )}
              {executed?.survivedFindings?.length ? (
                <Alert severity="warning" sx={{ mt: 0.5 }}>{executed.survivedFindings.length} findings survived and were surfaced.</Alert>
              ) : null}
            </Box>
          );
        })}
      </Stack>

      {report.truncated && (
        <Alert severity="info" sx={{ mt: 1 }} data-testid={TASKS_PANEL.strategyRepairReportTruncation}>
          Report caps applied: {Object.entries(report.truncated).map(([key, value]) => `${key} +${value}`).join(' · ')}
        </Alert>
      )}
      {report.refusal && <Alert severity="warning" sx={{ mt: 1 }}>{report.refusal.code}: strategy status {report.refusal.strategyStatus}</Alert>}
      {report.error && <Alert severity="error" sx={{ mt: 1 }}>{report.error}</Alert>}
    </Box>
  );
};

export const TaskDetailDialog: React.FC<TaskDetailDialogProps> = ({
  taskId,
  open,
  onClose,
  onNavigate,
  renderResultActions,
}) => {
  const { task } = useTask(taskId);
  // Ruling GP-R32 / final review FR-10: the section heading below speaks the language of the
  // panel it stands over — the resolver decides which that is, and this follows the same
  // live registry it does.
  const resolverLang = useTaskErrorResolverPanelLang(task as any);
  const projectId = (task as any)?.projectId as string | undefined;
  const { calls: aiCalls } = useTaskAiCalls(projectId, task?.id);

  const sortedPhases = useMemo(() => {
    if (!task?.phases) return [];
    return Object.entries(task.phases)
      .sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0));
  }, [task?.phases]);

  const aiCallsByPhase = useMemo(() => {
    const grouped = new Map<string, AiCallDoc[]>();
    for (const call of aiCalls) {
      const key = call.taskPhaseKey || call.phase;
      const existing = grouped.get(key) ?? [];
      existing.push(call);
      grouped.set(key, existing);
    }
    return grouped;
  }, [aiCalls]);

  const unmatchedAiCalls = useMemo(() => {
    if (!task?.phases) return aiCalls;
    return aiCalls.filter((call) => {
      const key = call.taskPhaseKey || call.phase;
      return !(key in task.phases!);
    });
  }, [aiCalls, task?.phases]);

  const config = TASK_TYPE_CONFIG[task?.taskType ?? ''];
  const billing = task ? getTaskBilling(task) : undefined;
  const billingCurrency = billing?.currency || 'usd';

  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 3,
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {config?.icon &&
        createElement(config.icon as React.ComponentType<SvgIconProps>, {
          sx: { fontSize: 24, color: 'text.secondary' },
        })}
      <Typography variant="h6" sx={{ flex: 1 }} noWrap>
        {task?.action ?? 'Task Details'}
      </Typography>
      {task && (
        <Chip
          label={STATUS_LABELS[task.status] ?? task.status}
          size="small"
          sx={{
            bgcolor: STATUS_COLORS[task.status] ?? 'text.disabled',
            color: 'white',
          }}
        />
      )}
      <IconButton onClick={onClose} size="small">
        <CloseIcon />
      </IconButton>
    </Box>
  );

  if (!task) {
    return (
      <BaseDialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth header={header}>
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary">Task not found</Typography>
        </Box>
      </BaseDialog>
    );
  }

  return (
    <BaseDialog
      open={open}
      onClose={() => onClose()}
      maxWidth="md"
      fullWidth
      header={header}
      data-testid={TASKS_PANEL.taskDetailDialog}
    >
      <Box sx={{ p: 3, overflow: 'auto', maxHeight: '70vh' }}>
        {/* Metadata */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
          <MetaField label="Task ID" value={task.id} />
          <MetaField label="Project ID" value={projectId ?? '—'} />
          <MetaField label="Type" value={config?.label ?? task.taskType ?? '—'} />
          <MetaField label="Status" value={STATUS_LABELS[task.status] ?? task.status} />
          <MetaField label="Started" value={formatFullTimestamp(task.createdAt)} />
          <MetaField label="Updated" value={formatFullTimestamp(task.updatedAt)} />
          {task.userId && <MetaField label="Started by" value={task.userId} />}
          {task.provider && <MetaField label="Provider" value={task.provider} />}
          {task.model && <MetaField label="Model" value={task.model} />}
        </Box>

        {task.description && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="body2" color="text.secondary">
              {task.description}
            </Typography>
          </>
        )}

        {/* Phases */}
        {sortedPhases.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Phases
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {sortedPhases.map(([key, phase]) => (
                <PhaseRow
                  key={key}
                  phaseKey={key}
                  phase={phase}
                  aiCalls={aiCallsByPhase.get(key) ?? []}
                />
              ))}
            </Box>
          </>
        )}

        {(task as any).strategyHealthReport && (
          <StrategyHealthTaskSection
            task={task}
            report={(task as any).strategyHealthReport as CliStrategyHealthReport}
            onNavigate={onNavigate}
          />
        )}

        {(task as any).repairReport && (
          <StrategyRepairReportSection report={(task as any).repairReport as CliRepairReport} />
        )}

        {unmatchedAiCalls.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ my: 1.5 }} />
            <AiCallDebugBlocks
              calls={unmatchedAiCalls}
              title={`AI debug not mapped to a phase (${unmatchedAiCalls.length})`}
            />
          </Box>
        )}

        {/* Error — resolver renders actionable CTAs per errorCode; raw error
            text is still shown for unknown codes via the generic fallback.
            G6a fix round 1 (I2): the condition is the SAME `error || errorCode`
            the resolver and `taskErrorResolverOffersOpenEntity` key on. Gating
            this section on `task.error` alone made a task carrying only an
            `errorCode` (an empty `error.message`) render no resolver AND lose
            its footer «Open …» to the predicate — no action, no link, no
            copy-id: the FP-6 dead end Must-fix #1/#2 removed, back on a narrow
            branch. One predicate, one condition, everywhere. */}
        {(task.error || (task as { errorCode?: string }).errorCode) && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
              {resolverLang === 'ru' ? 'Ошибка — что сделать дальше' : 'Error — what to do next'}
            </Typography>
            <TaskErrorResolver task={task as any} onNavigate={onNavigate} />
            <StrategyContractRepairPanel task={task} />
          </>
        )}

        {/* Cancel reason */}
        {task.cancelReason && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Cancel Reason
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {task.cancelReason}
            </Typography>
          </>
        )}

        {/* Token Usage and billing */}
        {(task.usage || billing) && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Token Usage
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {task.usage?.inputTokens != null && (
                <Typography variant="body2" color="text.secondary">
                  Input: {task.usage.inputTokens.toLocaleString()}
                </Typography>
              )}
              {task.usage?.outputTokens != null && (
                <Typography variant="body2" color="text.secondary">
                  Output: {task.usage.outputTokens.toLocaleString()}
                </Typography>
              )}
              {task.estimatedCostUsd != null && (
                <Typography variant="body2" color="text.secondary">
                  Provider cost: ${task.estimatedCostUsd.toFixed(4)}
                </Typography>
              )}
              {billing?.customerAmountMinor != null && (
                <Typography variant="body2" color="text.secondary">
                  Customer billing: {formatMoneyMinor(billing.customerAmountMinor, billingCurrency)}
                </Typography>
              )}
              {billing?.creditDebitMinor != null && (
                <Typography variant="body2" color={billing.creditDebitMinor > 0 ? 'error.main' : 'text.secondary'}>
                  Credits debited: {formatMoneyMinor(billing.creditDebitMinor, billingCurrency)}
                </Typography>
              )}
              {billing?.quotaQuantity != null && (
                <Typography variant="body2" color="text.secondary">
                  Quota covered: {billing.quotaQuantity.toLocaleString()} tokens
                </Typography>
              )}
              {billing?.creditQuantity != null && (
                <Typography variant="body2" color="text.secondary">
                  Credit billed: {billing.creditQuantity.toLocaleString()} tokens
                </Typography>
              )}
            </Box>
            {billing?.usageEventId && (
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
                Billing event: {billing.usageEventId}
                {billing.sourceHandler ? ` · ${billing.sourceHandler}` : ''}
              </Typography>
            )}
          </>
        )}

        {/* Result */}
        {task.result && Object.keys(task.result).length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            {renderResultActions?.(task)}
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Result
            </Typography>
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                maxHeight: 200,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(task.result, null, 2)}
            </Box>
          </>
        )}

        {/* Entity link — G6a design review (n-GP-2, Must fix): a FAILED task already
            carries this exact «Open …» button inside the resolver, next to the action
            it belongs to. Rendering it again here put two identical outlined buttons a
            divider apart (and, in the no-action states, made them the only two controls
            on the surface). One affordance, at the point of decision. */}
        {/* …and `onNavigate` is what makes the press DO something: the billing
            ledger mounts this dialog without one (BillingCatalogAdminPage), where
            the button rendered and clicking it called `onNavigate?.()` — nothing.
            A button exists only if pressing it acts (G5's own rule). */}
        {task.targetEntityPath?.frontendUrl && onNavigate && !taskErrorResolverOffersOpenEntity(task, onNavigate) && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => onNavigate?.(task.targetEntityPath!.frontendUrl!)}
            >
              Open {task.targetEntityPath.entityType ?? 'Result'}
            </Button>
          </>
        )}
      </Box>
    </BaseDialog>
  );
};

// Simple metadata field
const MetaField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.disabled">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
  </Box>
);
